import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, EyeOff } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import {
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarMenuSubButton,
    useSidebar,
} from 'src/shared/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu';

/**
 * Opens a group when it becomes active on the same page. Its active item can
 * arrive after the page does, as when it waits for the product settings, and
 * no navigation follows to open it. On mount, the group's own initial state
 * stands.
 */
const useOpenWhenActivated = (isActive: boolean, setOpen: (open: boolean) => void) => {
    const wasActive = useRef(isActive);

    useEffect(() => {
        if (isActive && !wasActive.current) {
            setOpen(true);
        }
        wasActive.current = isActive;
    }, [isActive, setOpen]);
};

export type NestedSidebarMenuLeafItem = {
    title: string;
    testId?: string;
    isActive?: boolean;
    /** In-app route: renders a <Link>, so ctrl/cmd/middle-click and right-click "open in new tab" work. */
    to?: string;
    /** External or full-page URL: renders a plain <a>, since react-router's <Link> can't navigate off-app. */
    href?: string;
    /** Side effect fired alongside `to`/`href`, or the sole action when neither is set (e.g. opening a dialog). */
    onClick?: () => void;
    /**
     * The item's product is set to Hidden for this organisation. Only internal staff still see
     * such an item, so the mark tells them that customers do not.
     */
    isHiddenFromCustomers?: boolean;
};

export type NestedSidebarMenuGroupItem = {
    title: string;
    testId?: string;
    subItems: NestedSidebarMenuLeafItem[];
};

export type NestedSidebarMenuSubItem = NestedSidebarMenuLeafItem | NestedSidebarMenuGroupItem;

export const isSubGroup = (item: NestedSidebarMenuSubItem): item is NestedSidebarMenuGroupItem => 'subItems' in item;

const HIDDEN_FROM_CUSTOMERS = 'Hidden from customers of this organisation';

/**
 * A quiet eye-off at the right edge of a page that customers of this organisation do not see.
 * Only pages carry it: on a group or menu row it would compete with the chevron.
 */
export const HiddenFromCustomersMark = () => (
    <span role='img' aria-label={HIDDEN_FROM_CUSTOMERS} title={HIDDEN_FROM_CUSTOMERS} className='ml-auto inline-flex shrink-0 text-grey-60'>
        <EyeOff size={12} strokeWidth={1.5} aria-hidden />
    </span>
);

/** The title of an item, and the mark at the right edge when customers do not see it. */
const ItemTitle = ({ title, isHiddenFromCustomers }: { title: string; isHiddenFromCustomers?: boolean }) => (
    <>
        <span>{title}</span>
        {isHiddenFromCustomers && <HiddenFromCustomersMark />}
    </>
);

type NestedSidebarMenuLeafControlProps = React.HTMLAttributes<HTMLElement> & {
    item: NestedSidebarMenuLeafItem;
};

// Renders a leaf item as whichever control actually matches its destination: a <Link> for an
// in-app route, a plain <a> for an external/full-page URL, or a <button> when it's an action with
// no destination at all (e.g. opening a dialog). Shared by every place a leaf is rendered — the
// expanded and collapsed variants, at both the top level and nested inside a sub-group.
//
// Rendered as the `asChild` target of SidebarMenuSubButton/DropdownMenuItem, both of which merge
// extra props (data-active, Radix's own onClick/keyboard handlers, refs) onto whatever single
// element they're given — so this must forward ref and spread the rest through to the real DOM
// node, or those merged props are silently dropped.
const NestedSidebarMenuLeafControl = React.forwardRef<HTMLElement, NestedSidebarMenuLeafControlProps>(({ item, ...rest }, ref) => {
    const handleClick: React.MouseEventHandler<HTMLElement> = event => {
        item.onClick?.();
        rest.onClick?.(event);
    };

    if (item.to) {
        return (
            <Link ref={ref as React.Ref<HTMLAnchorElement>} {...rest} to={item.to} onClick={handleClick} data-testid={item.testId}>
                <ItemTitle title={item.title} isHiddenFromCustomers={item.isHiddenFromCustomers} />
            </Link>
        );
    }
    if (item.href) {
        return (
            <a ref={ref as React.Ref<HTMLAnchorElement>} {...rest} href={item.href} onClick={handleClick} data-testid={item.testId}>
                <ItemTitle title={item.title} isHiddenFromCustomers={item.isHiddenFromCustomers} />
            </a>
        );
    }
    return (
        <button ref={ref as React.Ref<HTMLButtonElement>} type='button' {...rest} onClick={handleClick} data-testid={item.testId}>
            <ItemTitle title={item.title} isHiddenFromCustomers={item.isHiddenFromCustomers} />
        </button>
    );
});
NestedSidebarMenuLeafControl.displayName = 'NestedSidebarMenuLeafControl';

type NestedSidebarMenuItemProps = {
    label: string;
    testId?: string;
    icon: React.ReactNode;
    isActive: boolean;
    subItems: NestedSidebarMenuSubItem[];
    defaultOpen?: boolean;
    expandedButtonClassName?: string;
    collapsedButtonClassName?: string;
    labelClassName?: string;
};

const NestedSidebarMenuSubGroup = ({ item }: { item: NestedSidebarMenuGroupItem }) => {
    const isActive = item.subItems.some(subItem => subItem.isActive);
    const [open, setOpen] = useState(isActive);
    const isFirstRender = useRef(true);
    const { pathname } = useLocation();

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setOpen(isActive);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);
    useOpenWhenActivated(isActive, setOpen);

    return (
        <SidebarMenuSubItem>
            <Collapsible open={open} onOpenChange={setOpen} className='group/nested-collapsible'>
                <CollapsibleTrigger asChild>
                    <SidebarMenuSubButton asChild isActive={isActive && !open} data-testid={item.testId}>
                        <button type='button' className='flex w-full items-center gap-2'>
                            <span>{item.title}</span>
                            <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/nested-collapsible:rotate-90' />
                        </button>
                    </SidebarMenuSubButton>
                </CollapsibleTrigger>
                <CollapsibleContent className='overflow-hidden pr-1 data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up'>
                    <SidebarMenuSub className='mr-0 pr-0'>
                        {item.subItems.map(subItem => (
                            <SidebarMenuSubItem key={subItem.title}>
                                <SidebarMenuSubButton asChild isActive={subItem.isActive}>
                                    <NestedSidebarMenuLeafControl item={subItem} className='w-full' />
                                </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </Collapsible>
        </SidebarMenuSubItem>
    );
};

const DropdownMenuSubGroupItem = ({ item }: { item: NestedSidebarMenuGroupItem }) => (
    <DropdownMenuSub>
        <DropdownMenuSubTrigger
            data-testid={item.testId}
            data-active={item.subItems.some(subItem => subItem.isActive)}
            className='data-[active=true]:bg-sidebar-accent data-[active=true]:text-foreground'
        >
            <span>{item.title}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
            {item.subItems.map(subItem => (
                <DropdownMenuItem
                    key={subItem.title}
                    asChild
                    data-active={subItem.isActive}
                    className='focus:bg-secondary focus:text-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-foreground'
                >
                    <NestedSidebarMenuLeafControl item={subItem} />
                </DropdownMenuItem>
            ))}
        </DropdownMenuSubContent>
    </DropdownMenuSub>
);

// SidebarMenuItem (<li>) is always the outermost element here, with the
// Collapsible/DropdownMenu wrapper nested inside it. This keeps SidebarMenu
// (<ul>) valid HTML — it only ever has <li> children, never a <div> sibling.
const NestedSidebarMenuItem = ({
    label,
    testId,
    icon,
    isActive,
    subItems,
    defaultOpen,
    expandedButtonClassName,
    collapsedButtonClassName,
    labelClassName,
}: NestedSidebarMenuItemProps) => {
    const { state, isMobile } = useSidebar();
    const { pathname } = useLocation();
    const [open, setOpen] = useState(defaultOpen ?? false);
    const isFirstRender = useRef(true);

    // Respect defaultOpen on mount (even if isActive is already true then); only sync open state to
    // isActive afterwards. Keyed on pathname rather than isActive itself: a group opened manually
    // while inactive (e.g. browsing) never has an isActive transition to react to, so it would
    // otherwise stay open forever once navigation moves on to unrelated pages.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setOpen(isActive);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);
    useOpenWhenActivated(isActive, setOpen);

    if (state === 'collapsed' && !isMobile) {
        return (
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton tooltip={label} isActive={isActive} data-testid={testId} className={collapsedButtonClassName}>
                            {icon}
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side='right' align='start'>
                        <DropdownMenuLabel>{label}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            {subItems.map(item =>
                                isSubGroup(item) ? (
                                    <DropdownMenuSubGroupItem key={item.title} item={item} />
                                ) : (
                                    <DropdownMenuItem
                                        key={item.title}
                                        asChild
                                        data-active={item.isActive}
                                        className='focus:bg-secondary focus:text-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-foreground'
                                    >
                                        <NestedSidebarMenuLeafControl item={item} />
                                    </DropdownMenuItem>
                                )
                            )}
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        );
    }

    return (
        <SidebarMenuItem>
            <Collapsible open={open} onOpenChange={setOpen} className='group/collapsible'>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                        tooltip={label}
                        isActive={isActive && !open}
                        data-testid={testId}
                        className={expandedButtonClassName}
                    >
                        {icon}
                        <span className={labelClassName}>{label}</span>
                        <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent className='overflow-hidden pr-1 data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up'>
                    <SidebarMenuSub className='mr-0 pr-0'>
                        {subItems.map(item =>
                            isSubGroup(item) ? (
                                <NestedSidebarMenuSubGroup key={item.title} item={item} />
                            ) : (
                                <SidebarMenuSubItem key={item.title}>
                                    <SidebarMenuSubButton asChild isActive={item.isActive}>
                                        <NestedSidebarMenuLeafControl item={item} className='w-full' />
                                    </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                            )
                        )}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </Collapsible>
        </SidebarMenuItem>
    );
};

export default NestedSidebarMenuItem;
